import { zodResolver } from '@hookform/resolvers/zod';
import { SIGNUP_HEARD_ABOUT_SOURCES, SIGNUP_ROLES } from '@server/signup-answers';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { LinkNotValid } from '../components/nativeSignIn/LinkNotValid';
import { MfaCodeStep } from '../components/nativeSignIn/MfaCodeStep';
import { MfaSetupRequired } from '../components/nativeSignIn/MfaSetupRequired';
import { RegionRadioGroup } from '../components/nativeSignIn/RegionRadioGroup';
import { RegionScopeTooltip } from '../components/nativeSignIn/RegionScopeTooltip';
import { RequireNativeSignIn } from '../components/nativeSignIn/RequireNativeSignIn';
import { TextLink } from '../components/nativeSignIn/TextLink';
import { useLinkParams } from '../hooks/useLinkParams';
import { isSigningIn, useSignIn } from '../hooks/useSignIn';
import { PASSWORD_RULE_HINT, signupCompleteSchema, type SignupCompleteValues } from '../lib/authFormSchema';
import type { SignupAnswers } from '../lib/lookout-session';
import {
  ACCOUNT_EXISTS_TEXT,
  createRudderstackAccount,
  type LoginCredentials,
  type MfaChallenge,
  RudderstackAuthError,
} from '../lib/rudderstack-auth-client';
import {
  INVITE_EXPIRED,
  InviteCheckError,
  INVITEE_LINK_EXPIRED,
  inviteIdOf,
  inviteLoginPath,
  inviteSignupPath,
  renewInvite,
} from '../lib/signup-invite';
import { REGION_FIELD_LABEL, REGION_HINT } from '../lib/signup-regions';
import { webAppSignupLink } from '../lib/web-app-links';
import type { NativeSignInConfig } from '../types';

/**
 * `/signup/complete?token=…[&invitee=1&inviteId=…]`: the page the sign-up email links to. `type` is ignored: always
 * the Free Tier plan.
 */
export function SignupComplete(): React.ReactElement {
  usePageTitle('Create account');
  // First, so the token leaves the URL before anything records it.
  const link = useLinkParams();
  const token = link.get('token');
  const invitee = link.get('invitee') === '1';
  const inviteId = invitee ? inviteIdOf(link.get('inviteId')) : null;

  return (
    <RequireNativeSignIn webAppLink={(webAppUrl) => (token ? webAppSignupLink(webAppUrl, token) : null)}>
      {(config) =>
        token ? (
          <SignupCompleteForm config={config} token={token} invitee={invitee} inviteId={inviteId} />
        ) : (
          <LinkNotValid actionLabel="Start over" actionTo={startOverPath(inviteId)} />
        )
      }
    </RequireNativeSignIn>
  );
}

const REGION_LABEL_ID = 'signup-region-label';

/** The sign-up page, which sends a new link. An invitee starts over from the invite, as the invite page does. */
function startOverPath(inviteId: string | null): string {
  return inviteId ? inviteSignupPath(inviteId) : '/signup';
}

// config-backend's answer when the link's account exists, for example after the invitee joined the workspace.
const ACCOUNT_EXISTS = 'User already signed up';
// config-backend's answer when the email's company turned on SSO after the link. A new link fails the same way.
const SSO_ACCOUNT = 'Account already exists. Please sign in with SSO instead.';

/** config-backend's refusal of the account. Only an expired or used link gets "Start over". */
function CreateAccountError({ error, inviteId }: { error: Error; inviteId: string | null }): React.ReactElement {
  const reason = error instanceof RudderstackAuthError ? error.reason : undefined;
  if (reason === ACCOUNT_EXISTS) {
    return (
      <p>
        {ACCOUNT_EXISTS_TEXT} <TextLink to={inviteLoginPath(inviteId)}>Sign in</TextLink> instead.
      </p>
    );
  }
  if (error instanceof InviteCheckError || reason === SSO_ACCOUNT) return <p>{error.message}</p>;
  return (
    <>
      <p>{error.message}</p>
      <p className="mt-1">
        <TextLink to={startOverPath(inviteId)}>Start over</TextLink> to get a new link.
      </p>
    </>
  );
}

type Step =
  | { kind: 'form' }
  | { kind: 'mfa'; credentials: LoginCredentials; challenge: MfaChallenge }
  | { kind: 'mfa_setup' };

function signupAnswers(values: SignupCompleteValues): SignupAnswers {
  return {
    role: values.role,
    ...(values.heardAboutSource ? { heardAboutSource: values.heardAboutSource } : {}),
  };
}

function SignupCompleteForm({
  config,
  token,
  invitee,
  inviteId,
}: {
  config: NativeSignInConfig;
  token: string;
  invitee: boolean;
  inviteId: string | null;
}): React.ReactElement {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'form' });
  const schema = useMemo(() => signupCompleteSchema(invitee), [invitee]);
  const form = useForm<SignupCompleteValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { name: '', organization: '', password: '', confirmPassword: '', region: 'US' },
  });
  const createAccount = useMutation({
    mutationFn: async (values: SignupCompleteValues) => {
      // config-backend creates an invitee's account only while Lookout's grant for the invite is live (ACT2-887).
      if (inviteId) await renewInvite(inviteId);
      try {
        await createRudderstackAccount(config.apiBaseUrl, {
          name: values.name,
          token,
          password: values.password,
          organization: invitee ? undefined : values.organization,
          region: invitee ? undefined : values.region,
        });
      } catch (err) {
        // A new sign-up link does not revive an invite that config-backend refused.
        if (invitee && err instanceof Error && err.message === INVITE_EXPIRED) {
          throw new InviteCheckError(inviteId ? err.message : INVITEE_LINK_EXPIRED);
        }
        throw err;
      }
    },
    gcTime: 0,
  });
  // No return path: Lookout lands a new user on their pending invite or on workspace creation.
  const signIn = useSignIn(config.apiBaseUrl, null);
  const busy = createAccount.isPending || isSigningIn(signIn);
  const toLogin = (): void => void navigate('/login');

  // `created` is what the submit that created the account sent. A retry uses it, not later edits to the form.
  const signInNow = (created: SignupCompleteValues): void => {
    const credentials: LoginCredentials = { token, password: created.password };
    signIn.mutate(
      { ...credentials, signup: signupAnswers(created) },
      {
        onSuccess: (outcome) => {
          if (outcome.kind === 'mfa_challenge') setStep({ kind: 'mfa', credentials, challenge: outcome.challenge });
          if (outcome.kind === 'mfa_setup') setStep({ kind: 'mfa_setup' });
        },
      },
    );
  };

  const onValid = (values: SignupCompleteValues): void => {
    // A retry after a failed sign-in must not create the account twice.
    if (createAccount.isSuccess && createAccount.variables) signInNow(createAccount.variables);
    else createAccount.mutate(values, { onSuccess: (_created, submitted) => signInNow(submitted) });
  };

  const error = createAccount.error ?? signIn.error;

  return (
    // On a short screen only the content scrolls, and the brand stays in place. `relative` keeps the hidden
    // native inputs of Radix controls inside the scroll area, so they do not make the page taller.
    <GatewayShell className="flex max-h-[calc(100dvh-5rem)] flex-col">
      <GatewayBrand poweredBy />
      <div className="relative -mx-1 scrollbar-none min-h-0 flex-1 overflow-y-auto px-1">
        {step.kind === 'mfa' ? (
          <MfaCodeStep
            apiBaseUrl={config.apiBaseUrl}
            credentials={step.credentials}
            challenge={step.challenge}
            returnTo={null}
            onSetupRequired={() => setStep({ kind: 'mfa_setup' })}
            onBack={toLogin}
          />
        ) : step.kind === 'mfa_setup' ? (
          <MfaSetupRequired webAppUrl={config.webAppUrl} onBack={toLogin} />
        ) : (
          <GatewayContent
            title="Finish creating your account"
            description={
              invitee
                ? 'Tell us about you and set a password to join the workspace.'
                : 'Tell us about you and set a password.'
            }
          >
            <Form {...form}>
              <form
                noValidate
                aria-label="Finish creating your account"
                className="flex flex-col gap-4 text-left"
                onSubmit={form.handleSubmit(onValid)}
              >
                {error && (
                  <Callout variant="error" role="alert">
                    {createAccount.error ? (
                      <CreateAccountError error={createAccount.error} inviteId={inviteId} />
                    ) : (
                      <p>{error.message}</p>
                    )}
                  </Callout>
                )}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input {...field} size="lg" autoComplete="name" maxLength={50} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!invitee && (
                  <FormField
                    control={form.control}
                    name="organization"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization name</FormLabel>
                        <FormControl>
                          <Input {...field} size="lg" autoComplete="organization" maxLength={50} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your role</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger size="lg" className="w-full">
                            <SelectValue placeholder="Select one…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SIGNUP_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" size="lg" autoComplete="new-password" />
                      </FormControl>
                      <FormDescription className="text-xs">{PASSWORD_RULE_HINT}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input {...field} type="password" size="lg" autoComplete="new-password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!invitee && (
                  <FormField
                    control={form.control}
                    name="region"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-1">
                          <FormLabel id={REGION_LABEL_ID}>{REGION_FIELD_LABEL}</FormLabel>
                          <RegionScopeTooltip />
                        </div>
                        <FormDescription className="text-xs">{REGION_HINT}</FormDescription>
                        <FormControl>
                          {/* A `<label for>` names only a form control, not the group's `div`. */}
                          <RegionRadioGroup
                            aria-labelledby={REGION_LABEL_ID}
                            value={field.value}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="heardAboutSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Where did you hear about us?{' '}
                        <span className="font-normal text-muted-foreground">(Optional)</span>
                      </FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger size="lg" className="w-full">
                            <SelectValue placeholder="Select one…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SIGNUP_HEARD_ABOUT_SOURCES.map((source) => (
                            <SelectItem key={source} value={source}>
                              {source}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
                  {busy ? 'Creating account…' : 'Create account'}
                </Button>
              </form>
            </Form>
            <p className="mt-4 text-xs text-muted-foreground">
              Already have an account? <TextLink to={inviteLoginPath(inviteId)}>Sign in</TextLink>
            </p>
          </GatewayContent>
        )}
      </div>
    </GatewayShell>
  );
}
